import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    MenuItem,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import AccountLifecycleBadge from '../../components/AccountLifecycleBadge.comp';
import GroupedMultiSelect from '../../components/GroupedMultiSelect.comp';
import {
    createTeacherService,
    editTeacherService,
    deleteTeacherService,
    publishTeachersService,
} from '../../../../services/teacher.service';
import { toast } from '../../../../utils/utils';
import { TOAST_TYPES, ACCOUNT_LIFECYCLE_STATUS } from '../../../../utils/constants';

/**
 * Manage LearnCreator Teachers — My Classes deck, 30 Sep 2026 (A2-A13, B1-B3),
 * built to the HTML flow "PbDQ_SNW, SW_My Classes_Flow".
 *
 *   Existing Teachers tab   search, class filter, sortable; Edit / Remove;
 *                           "View/edit class list"; Publish Workspace
 *   Add Teachers tab        row entry, classes optional, confirm
 *
 * Nothing here emails anyone except Publish Workspace: adding, editing and
 * assigning classes leave a teacher DRAFT (the A5 confirm says so). A teacher
 * is PUBLISHED once they have been sent their activation email, which the
 * server reports as `workspace_status`.
 *
 * `classes` is the per-class list from the learning-space preview — one call
 * that already carries grade, learner count and publication state for every
 * class, which the class pickers need and the paged class table does not have.
 */

const ORANGE = 'var(--brand-primary, #FF6900)';
const MUTED = 'var(--brand-text-muted, #6b6b6b)';
const BORDER = 'var(--brand-border, #e3e0dc)';
const DANGER = '#c0392b';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMPTY_ROW = { firstName: '', lastName: '', email: '', sectionCodes: [] };

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : (/(s|sh|ch|x)$/.test(word) ? 'es' : 's')}`;
const isPublishedTeacher = (t) => t?.workspace_status === 'published' || !!t?.credentials_sent_at;
const fullName = (t) => [t?.first_name, t?.last_name].filter(Boolean).join(' ').trim() || t?.email || '';

export const gradeLabel = (grade) => {
    const g = String(grade || '').trim();
    if (!g) return 'No grade';
    return /^\d+$/.test(g) ? `Grade ${g}` : g;
};

const classLabel = (c) => {
    const name = String(c?.section_name || c?.section_code || '').trim();
    return /^class\b/i.test(name) ? name : `Class ${name}`;
};


/** Kindergarten first, then by the number in the grade, then by class name. */
const gradeRank = (grade) => {
    const g = String(grade || '');
    if (/^k(inder|g)/i.test(g)) return -1;
    const n = g.match(/\d+/);
    return n ? Number(n[0]) : Number.MAX_SAFE_INTEGER;
};
export const sortClassesByGrade = (classes) => [...(classes || [])].sort((a, b) =>
    (gradeRank(a.grade) - gradeRank(b.grade))
    || String(a.grade || '').localeCompare(String(b.grade || ''))
    || String(a.section_name || '').localeCompare(String(b.section_name || ''), undefined, { numeric: true }));

/** Options for GroupedMultiSelect, grouped by grade (lowest first). */
export const classOptions = (classes, { exclude = [], schoolNameFor = null, disablePublished = false } = {}) => {
    const skip = new Set(exclude.map(String));
    return sortClassesByGrade(classes)
        .filter((c) => !skip.has(String(c.section_code)))
        .map((c) => {
            const school = schoolNameFor ? schoolNameFor(c) : '';
            return {
                value: String(c.section_code),
                label: school ? `${classLabel(c)} · ${school}` : classLabel(c),
                group: gradeLabel(c.grade),
                meta: plural(Number(c.student_count) || 0, 'learner'),
                disabled: disablePublished && !!c.is_published,
                disabledLabel: 'Published',
            };
        });
};

const pillBtn = {
    borderRadius: '22px', textTransform: 'none', fontWeight: 700, fontSize: '14px', px: 2.5, py: 0.9,
};
const primaryBtn = {
    ...pillBtn, backgroundColor: ORANGE, color: '#fff', boxShadow: 'none',
    '&:hover': { backgroundColor: '#c9541e', boxShadow: 'none' },
    '&.Mui-disabled': { backgroundColor: 'rgba(241, 90, 36, 0.35)', color: '#fff' },
};
const secondaryBtn = {
    ...pillBtn, border: `1px solid ${BORDER}`, color: 'var(--brand-text, #111)', backgroundColor: 'transparent',
};
const smallBtn = {
    textTransform: 'none', fontSize: '12.5px', fontWeight: 600, minWidth: 0, px: 1.5, py: 0.4, borderRadius: '6px',
};
const publishBtn = {
    ...pillBtn, border: '2px solid #2f5bd3', color: ORANGE, backgroundColor: 'transparent',
    '&.Mui-disabled': { borderColor: BORDER },
};
const cellSx = { fontSize: '13.5px', padding: '12px 10px', borderBottom: `1px solid ${BORDER}`, verticalAlign: 'middle' };
const headSx = { ...cellSx, fontWeight: 700, textAlign: 'left', whiteSpace: 'nowrap', backgroundColor: 'rgba(0,0,0,0.02)' };
const inputSx = { '& .MuiInputBase-input': { fontSize: '13.5px', py: 1 } };

const TeacherChip = ({ published }) => (
    <AccountLifecycleBadge status={published ? ACCOUNT_LIFECYCLE_STATUS.PUBLISHED : ACCOUNT_LIFECYCLE_STATUS.DRAFT} />
);

/** Small centred yes/no dialog used by every confirm step. */
const ConfirmDialog = ({ open, title, body, confirmLabel, onConfirm, onCancel, busy = false, danger = false, icon = null }) => (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '18px' } }}>
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 3 }}>
            {icon}
            <Typography sx={{ fontSize: '19px', fontWeight: 800 }}>{title}</Typography>
            {body && <Typography component="div" sx={{ fontSize: '13.5px', color: MUTED, mt: 1 }}>{body}</Typography>}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                <Button fullWidth onClick={onCancel} disabled={busy} sx={secondaryBtn}>Cancel</Button>
                <Button
                    fullWidth
                    onClick={onConfirm}
                    disabled={busy}
                    startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
                    sx={danger ? { ...primaryBtn, backgroundColor: DANGER, '&:hover': { backgroundColor: '#a93226' } } : primaryBtn}
                >
                    {confirmLabel}
                </Button>
            </Box>
        </DialogContent>
    </Dialog>
);

const DialogHeader = ({ title, subtitle, onClose }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '22px', fontWeight: 800 }}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: '13px', color: MUTED, mt: 0.5 }}>{subtitle}</Typography>}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
    </Box>
);

// ─── A6-A9: one teacher's classes ────────────────────────────────────────────

const TeacherClassesDialog = ({ open, teacher, classes, schoolNameFor, orgCode, onClose, onSaved }) => {
    const [assigned, setAssigned] = useState([]);   // codes as saved
    const [added, setAdded] = useState([]);         // staged additions
    const [removed, setRemoved] = useState([]);     // staged removals
    const [picker, setPicker] = useState([]);
    const [sortAsc, setSortAsc] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !teacher) return;
        setAssigned((teacher.section_codes || []).map(String));
        setAdded([]);
        setRemoved([]);
        setPicker([]);
        setSortAsc(true);
        setConfirming(false);
    }, [open, teacher]);

    const byCode = useMemo(() => new Map((classes || []).map((c) => [String(c.section_code), c])), [classes]);
    const rows = [...assigned, ...added]
        .map((code) => byCode.get(code) || { section_code: code, section_name: code, grade: '', student_count: 0 })
        .sort((a, b) => {
            const cmp = String(a.section_name || '').localeCompare(String(b.section_name || ''), undefined, { numeric: true });
            return sortAsc ? cmp : -cmp;
        });
    const remaining = assigned.length - removed.length + added.length;
    const dirty = added.length > 0 || removed.length > 0;

    const stageAdd = () => {
        setAdded((prev) => [...prev, ...picker.filter((c) => !prev.includes(c) && !assigned.includes(c))]);
        setRemoved((prev) => prev.filter((c) => !picker.includes(c)));
        setPicker([]);
    };
    const toggleRemove = (code) => {
        if (added.includes(code)) {
            setAdded((prev) => prev.filter((c) => c !== code));
            return;
        }
        setRemoved((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
    };

    const save = async () => {
        setSaving(true);
        try {
            const section_codes = [...assigned.filter((c) => !removed.includes(c)), ...added];
            const res = await editTeacherService({ org_code: orgCode, email: teacher.email, section_codes });
            if (res?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, 'Classes', `Classes updated for ${fullName(teacher)}.`);
                onSaved();
                onClose();
            }
        } catch {
            // invokeApi has already shown the error
        } finally {
            setSaving(false);
            setConfirming(false);
        }
    };

    if (!teacher) return null;

    const summary = [
        added.length ? `${plural(added.length, 'class')} will be added` : '',
        removed.length ? `${plural(removed.length, 'class')} will be removed` : '',
    ].filter(Boolean).join(' and ');

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}>
                <DialogContent>
                    <DialogHeader title={`Classes — ${fullName(teacher)}`} subtitle={teacher.email} onClose={onClose} />

                    <Typography sx={{ fontSize: '13px', fontWeight: 700, mt: 3, mb: 1 }}>Add classes</Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <GroupedMultiSelect
                            options={classOptions(classes, { exclude: [...assigned, ...added], schoolNameFor })}
                            value={picker}
                            onChange={setPicker}
                            placeholder="Select classes to add"
                        />
                        <Button onClick={stageAdd} disabled={!picker.length} sx={{ ...primaryBtn, minWidth: 80 }}>Add</Button>
                    </Box>

                    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', mt: 2, overflow: 'hidden' }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <Box component="th" sx={{ ...headSx, cursor: 'pointer' }} onClick={() => setSortAsc((v) => !v)}>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                            Class {sortAsc ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />}
                                        </Box>
                                    </Box>
                                    <Box component="th" sx={headSx}>Grade</Box>
                                    <Box component="th" sx={headSx}>Learners</Box>
                                    <Box component="th" sx={headSx} />
                                    <Box component="th" sx={headSx} />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr>
                                        <Box component="td" colSpan={5} sx={{ ...cellSx, textAlign: 'center', color: MUTED }}>
                                            No classes assigned. Classes are optional.
                                        </Box>
                                    </tr>
                                )}
                                {rows.map((c) => {
                                    const code = String(c.section_code);
                                    const struck = removed.includes(code);
                                    const strike = struck ? { textDecoration: 'line-through', color: MUTED } : {};
                                    return (
                                        <Box component="tr" key={code} sx={{ backgroundColor: struck ? 'rgba(192,57,43,0.05)' : 'transparent' }}>
                                            <Box component="td" sx={{ ...cellSx, ...strike }}>{c.section_name || code}</Box>
                                            <Box component="td" sx={{ ...cellSx, ...strike }}>{gradeLabel(c.grade)}</Box>
                                            <Box component="td" sx={{ ...cellSx, ...strike }}>{Number(c.student_count) || 0}</Box>
                                            <Box component="td" sx={cellSx}><TeacherChip published={!!c.is_published} /></Box>
                                            <Box component="td" sx={{ ...cellSx, textAlign: 'right' }}>
                                                <Button
                                                    onClick={() => toggleRemove(code)}
                                                    sx={{ ...smallBtn, border: `1px solid ${struck ? ORANGE : BORDER}`, color: struck ? ORANGE : 'var(--brand-text, #333)' }}
                                                >
                                                    {struck ? 'Undo' : 'Remove'}
                                                </Button>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </tbody>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3, pt: 2, borderTop: `1px solid ${BORDER}` }}>
                        <Typography sx={{ fontSize: '13px', color: MUTED, flex: 1 }}>
                            {plural(remaining, 'class')} assigned{dirty ? ' · unsaved changes' : ''}
                        </Typography>
                        <Button onClick={onClose} sx={secondaryBtn}>Cancel</Button>
                        <Button onClick={() => setConfirming(true)} disabled={!dirty} sx={primaryBtn}>Save changes</Button>
                    </Box>
                </DialogContent>
            </Dialog>
            <ConfirmDialog
                open={confirming}
                title="Save class changes?"
                body={`${summary.charAt(0).toUpperCase()}${summary.slice(1)} for ${fullName(teacher)}.`}
                confirmLabel="Save changes"
                onConfirm={save}
                onCancel={() => setConfirming(false)}
                busy={saving}
            />
        </>
    );
};

// ─── A10-A12: edit one teacher ───────────────────────────────────────────────

const EditTeacherDialog = ({ open, teacher, classes, orgCode, onClose, onSaved, onManageClasses }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !teacher) return;
        setFirstName(teacher.first_name || '');
        setLastName(teacher.last_name || '');
        setEmail(teacher.email || '');
        setConfirming(false);
    }, [open, teacher]);

    if (!teacher) return null;
    const locked = isPublishedTeacher(teacher);
    const byCode = new Map((classes || []).map((c) => [String(c.section_code), c]));
    const classNames = (teacher.section_codes || []).map((c) => byCode.get(String(c))?.section_name || c);

    const errors = {
        firstName: !firstName.trim() ? 'Required' : '',
        lastName: !lastName.trim() ? 'Required' : '',
        email: !email.trim() ? 'Required' : (EMAIL_RE.test(email.trim()) ? '' : 'Invalid email'),
    };
    const invalid = Object.values(errors).some(Boolean);
    const emailChanged = email.trim().toLowerCase() !== String(teacher.email).toLowerCase();

    const save = async () => {
        setSaving(true);
        try {
            const res = await editTeacherService({
                org_code: orgCode,
                email: teacher.email,
                ...(emailChanged && !locked ? { new_email: email.trim() } : {}),
                first_name: firstName.trim(),
                last_name: lastName.trim(),
            });
            if (res?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, 'LearnCreator Teachers', `Saved changes to ${firstName.trim()} ${lastName.trim()}.`);
                onSaved();
                onClose();
            }
        } catch {
            // invokeApi has already shown the error
        } finally {
            setSaving(false);
            setConfirming(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}>
                <DialogContent>
                    <DialogHeader title="Edit teacher" subtitle={fullName(teacher)} onClose={onClose} />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 3 }}>
                        <TextField
                            label="First Name" required size="small" value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            error={!!errors.firstName && firstName !== ''} helperText={firstName !== '' ? errors.firstName : ''}
                        />
                        <TextField
                            label="Last Name" required size="small" value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            error={!!errors.lastName && lastName !== ''} helperText={lastName !== '' ? errors.lastName : ''}
                        />
                    </Box>
                    <TextField
                        label="Teacher Email" required size="small" fullWidth sx={{ mt: 2 }}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={locked}
                        error={!locked && !!errors.email && email !== ''}
                        helperText={locked
                            ? "This workspace is published, so the email can't be changed."
                            : (email !== '' ? errors.email : '')}
                    />
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                        border: `1px solid ${BORDER}`, borderRadius: '10px', px: 2, py: 1.5, mt: 2,
                    }}>
                        <Typography sx={{ fontSize: '13px', color: MUTED }}>
                            Classes: <Box component="span" sx={{ color: 'var(--brand-text, #111)' }}>{classNames.length ? classNames.join(', ') : 'None'}</Box>
                        </Typography>
                        <Button onClick={onManageClasses} sx={{ ...smallBtn, color: ORANGE, whiteSpace: 'nowrap' }}>Manage classes</Button>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3, pt: 2, borderTop: `1px solid ${BORDER}` }}>
                        <Button onClick={onClose} sx={secondaryBtn}>Cancel</Button>
                        <Button onClick={() => setConfirming(true)} disabled={invalid} sx={primaryBtn}>Save</Button>
                    </Box>
                </DialogContent>
            </Dialog>
            <ConfirmDialog
                open={confirming}
                title={`Save changes to ${fullName(teacher)}?`}
                body="Their updated details will be saved."
                confirmLabel="Save"
                onConfirm={save}
                onCancel={() => setConfirming(false)}
                busy={saving}
            />
        </>
    );
};

// ─── B1-B3: Publish Workspace ────────────────────────────────────────────────

const PublishWorkspacesDialog = ({ open, teachers, classes, orgCode, onClose, onPublished }) => {
    const [selected, setSelected] = useState([]);
    const [confirming, setConfirming] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [result, setResult] = useState(null);

    const drafts = useMemo(() => (teachers || []).filter((t) => !isPublishedTeacher(t)), [teachers]);
    const byCode = useMemo(() => new Map((classes || []).map((c) => [String(c.section_code), c])), [classes]);

    useEffect(() => {
        if (!open) return;
        setSelected([]);
        setConfirming(false);
        setResult(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const allSelected = drafts.length > 0 && drafts.every((t) => selected.includes(t.email));
    const toggle = (email) => setSelected((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));

    const publish = async () => {
        setPublishing(true);
        try {
            const res = await publishTeachersService({ org_code: orgCode, emails: selected });
            const data = res?.data || {};
            const failed = (data.results || []).filter((r) => r.status === 'failed').map((r) => r.email);
            if (failed.length) {
                toast(TOAST_TYPES.WARN, 'Publish Workspace', `Could not email ${failed.join(', ')}. They are still in draft — publish again to retry.`);
            }
            setResult({ published: data.published || [], published_at: data.published_at });
            onPublished();
        } catch {
            // invokeApi has already shown the error
        } finally {
            setPublishing(false);
            setConfirming(false);
        }
    };

    const stamp = (() => {
        const d = result?.published_at ? new Date(result.published_at) : null;
        if (!d || Number.isNaN(d.getTime())) return null;
        return `Published on ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    })();

    if (result) {
        const n = result.published.length;
        return (
            <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
                <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 3 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: 'rgba(46,125,50,0.12)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CheckIcon style={{ fontSize: 28, color: '#2e7d32' }} />
                    </Box>
                    <Typography sx={{ fontSize: '21px', fontWeight: 800, mt: 2 }}>
                        {n === 1 ? 'Workspace published' : `${n} workspaces published`}
                    </Typography>
                    <Typography sx={{ fontSize: '13.5px', color: MUTED, mt: 1 }}>
                        {n > 0
                            ? 'Activation emails have been sent. Teachers can now sign in to their workspace and start using LearnCreator.'
                            : 'No activation emails could be sent.'}
                    </Typography>
                    {n > 0 && (
                        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', mt: 2.5, textAlign: 'left', overflow: 'hidden' }}>
                            {result.published.map((email, i) => (
                                <Box key={email} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderTop: i ? `1px solid ${BORDER}` : 'none' }}>
                                    <CheckIcon style={{ fontSize: 16, color: '#2e7d32' }} />
                                    <Typography sx={{ fontSize: '13px' }}>
                                        Activation email sent to <Box component="span" sx={{ fontFamily: 'monospace' }}>{email}</Box>
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                    {stamp && <Typography sx={{ fontSize: '12px', color: MUTED, mt: 2, fontFamily: 'monospace' }}>{stamp}</Typography>}
                    <Button onClick={onClose} sx={{ ...primaryBtn, mt: 2.5 }}>Back to LearnCreator teachers</Button>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}>
                <DialogContent>
                    <DialogHeader
                        title="Publish workspaces"
                        subtitle="Choose the teachers to publish. Once published, they will be able to access their workspaces and start using LearnCreator."
                        onClose={onClose}
                    />
                    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', mt: 2.5, overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, backgroundColor: 'rgba(0,0,0,0.03)' }}>
                            <Checkbox
                                size="small"
                                checked={allSelected}
                                indeterminate={!allSelected && selected.length > 0}
                                disabled={!drafts.length}
                                onChange={() => setSelected(allSelected ? [] : drafts.map((t) => t.email))}
                                sx={{ p: 0.5, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: ORANGE } }}
                            />
                            <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
                                Select all not yet published ({drafts.length})
                            </Typography>
                        </Box>
                        {(teachers || []).map((t) => {
                            const published = isPublishedTeacher(t);
                            const codes = (t.section_codes || []).map((c) => byCode.get(String(c))?.section_name || c);
                            return (
                                <Box
                                    key={t.email}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1.25, borderTop: `1px solid ${BORDER}`,
                                        backgroundColor: selected.includes(t.email) ? 'rgba(241,90,36,0.04)' : 'transparent',
                                    }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={!published && selected.includes(t.email)}
                                        disabled={published}
                                        onChange={() => toggle(t.email)}
                                        sx={{ p: 0.5, '&.Mui-checked': { color: ORANGE } }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700 }}>{fullName(t)}</Typography>
                                        <Typography sx={{ fontSize: '12px', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {t.email}{codes.length ? ` · ${codes.join(', ')}` : ''}
                                        </Typography>
                                    </Box>
                                    {published && <TeacherChip published />}
                                </Box>
                            );
                        })}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '13px', color: MUTED, flex: 1 }}>
                            {selected.length
                                ? `${selected.length} selected · ${plural(selected.length, 'activation email')} will be sent`
                                : 'No teachers selected'}
                        </Typography>
                        <Button onClick={onClose} sx={secondaryBtn}>Cancel</Button>
                        <Button onClick={() => setConfirming(true)} disabled={!selected.length} sx={primaryBtn}>
                            {!selected.length ? 'Publish workspaces' : (selected.length === 1 ? 'Publish 1 workspace' : `Publish ${selected.length} workspaces`)}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
            <ConfirmDialog
                open={confirming}
                title={selected.length === 1 ? 'Publish this workspace?' : `Publish ${selected.length} workspaces?`}
                body={selected.length === 1
                    ? `${fullName((teachers || []).find((t) => t.email === selected[0]))} will get an activation email to sign in to their workspace and start using LearnCreator.`
                    : `${selected.length} teachers will get an activation email to sign in to their workspaces and start using LearnCreator.`}
                confirmLabel="Publish"
                onConfirm={publish}
                onCancel={() => setConfirming(false)}
                busy={publishing}
            />
        </>
    );
};

// ─── The dialog ──────────────────────────────────────────────────────────────

const ManageLearnCreatorTeachersDialog = ({
    open,
    onClose,
    teachers = [],
    classes = [],
    loading = false,
    orgCode = null,
    schoolNameFor = null,
    onChanged,
}) => {
    const [tab, setTab] = useState('existing');
    const [search, setSearch] = useState('');
    const [classFilter, setClassFilter] = useState('');
    const [sort, setSort] = useState({ key: 'class', asc: true });
    const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
    const [confirmAdd, setConfirmAdd] = useState(false);
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState(null);
    const [classesFor, setClassesFor] = useState(null);
    const [removing, setRemoving] = useState(null);
    const [removeBusy, setRemoveBusy] = useState(false);
    const [publishOpen, setPublishOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTab('existing');
        setSearch('');
        setClassFilter('');
        setRows([{ ...EMPTY_ROW }]);
    }, [open]);

    const byCode = useMemo(() => new Map((classes || []).map((c) => [String(c.section_code), c])), [classes]);
    const firstClassName = (t) => (t.section_codes || [])
        .map((c) => String(byCode.get(String(c))?.section_name || c))
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))[0] || '￿';

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        const list = (teachers || []).filter((t) => {
            if (classFilter && !(t.section_codes || []).map(String).includes(classFilter)) return false;
            if (!q) return true;
            return [t.first_name, t.last_name, t.email].some((v) => String(v || '').toLowerCase().includes(q));
        });
        const cmp = {
            name: (a, b) => fullName(a).localeCompare(fullName(b)),
            class: (a, b) => firstClassName(a).localeCompare(firstClassName(b), undefined, { numeric: true }),
            status: (a, b) => Number(isPublishedTeacher(a)) - Number(isPublishedTeacher(b)),
        }[sort.key];
        return [...list].sort((a, b) => (sort.asc ? cmp(a, b) : cmp(b, a)) || fullName(a).localeCompare(fullName(b)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teachers, search, classFilter, sort, byCode]);

    const existingEmails = new Set((teachers || []).map((t) => String(t.email).toLowerCase()));
    const rowError = (r, idx) => {
        const email = r.email.trim().toLowerCase();
        if (!r.firstName.trim() || !r.lastName.trim() || !email) return 'incomplete';
        if (!EMAIL_RE.test(email)) return 'Invalid email';
        if (existingEmails.has(email)) return 'Already a LearnCreator teacher';
        if (rows.some((o, j) => j !== idx && o.email.trim().toLowerCase() === email)) return 'Duplicate email';
        return '';
    };
    const filled = rows.filter((r) => r.firstName || r.lastName || r.email || r.sectionCodes.length);
    const ready = rows.filter((r, i) => (r.firstName || r.lastName || r.email) && !rowError(r, i));
    const blocked = rows.some((r, i) => (r.firstName || r.lastName || r.email) && rowError(r, i));
    const incomplete = filled.length - ready.length;

    const updateRow = (idx, field, value) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

    const addTeachers = async () => {
        setAdding(true);
        let ok = 0;
        try {
            for (const r of ready) {
                try {
                    const res = await createTeacherService({
                        org_code: orgCode,
                        email: r.email.trim().toLowerCase(),
                        first_name: r.firstName.trim(),
                        last_name: r.lastName.trim(),
                        section_codes: r.sectionCodes,
                        is_lc_teacher: true,
                    });
                    if (res?.data?.success) ok += 1;
                } catch {
                    // invokeApi has already shown why this one failed — carry on
                }
            }
        } finally {
            setAdding(false);
            setConfirmAdd(false);
        }
        if (ok) {
            toast(TOAST_TYPES.SUCCESS, 'LearnCreator Teachers', `${plural(ok, 'teacher')} added. Publish their workspace to send the activation email.`);
            setRows([{ ...EMPTY_ROW }]);
            setTab('existing');
            onChanged();
        }
    };

    const remove = async () => {
        setRemoveBusy(true);
        try {
            const res = await deleteTeacherService(removing.email, orgCode);
            if (res?.data?.success) {
                toast(TOAST_TYPES.SUCCESS, 'LearnCreator Teachers', `${fullName(removing)} was removed.`);
                onChanged();
            }
        } catch {
            // invokeApi has already shown the error
        } finally {
            setRemoveBusy(false);
            setRemoving(null);
        }
    };

    const SortHead = ({ k, children }) => (
        <Box
            component="th"
            sx={{ ...headSx, cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setSort((s) => ({ key: k, asc: s.key === k ? !s.asc : true }))}
        >
            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                {children}
                {sort.key === k && (sort.asc ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />)}
            </Box>
        </Box>
    );

    const removingClassNames = removing
        ? (removing.section_codes || []).map((c) => byCode.get(String(c))?.section_name || c)
        : [];

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: { xs: 0.5, sm: 2 } } }}>
                <DialogContent>
                    <DialogHeader
                        title="Manage LearnCreator Teachers"
                        subtitle="Add LearnCreator teachers and choose which classes each one teaches."
                        onClose={onClose}
                    />
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        sx={{
                            mt: 2, borderBottom: `1px solid ${BORDER}`,
                            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '15px' },
                            '& .Mui-selected': { color: `${ORANGE} !important` },
                            '& .MuiTabs-indicator': { backgroundColor: ORANGE },
                        }}
                    >
                        <Tab value="existing" label={`Existing Teachers (${teachers.length})`} />
                        <Tab value="add" label="Add Teachers" />
                    </Tabs>

                    {tab === 'existing' && (
                        <Box sx={{ mt: 2.5 }}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                                <TextField
                                    size="small" placeholder="Search by name or email…" value={search}
                                    onChange={(e) => setSearch(e.target.value)} sx={{ flex: '1 1 240px', ...inputSx }}
                                />
                                <TextField
                                    select size="small" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}
                                    SelectProps={{ displayEmpty: true }} sx={{ minWidth: 180, ...inputSx }}
                                >
                                    <MenuItem value="">All classes</MenuItem>
                                    {sortClassesByGrade(classes).map((c) => (
                                        <MenuItem key={c.section_code} value={String(c.section_code)}>{classLabel(c)}</MenuItem>
                                    ))}
                                </TextField>
                                <Box sx={{ flex: 1 }} />
                                <Tooltip title={teachers.some((t) => !isPublishedTeacher(t)) ? '' : 'Every teacher is already published.'} arrow>
                                    <span>
                                        <Button
                                            onClick={() => setPublishOpen(true)}
                                            disabled={!teachers.some((t) => !isPublishedTeacher(t))}
                                            sx={publishBtn}
                                        >
                                            Publish Workspace
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>

                            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', overflowX: 'auto' }}>
                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                                    <thead>
                                        <tr>
                                            <Box component="th" sx={{ ...headSx, width: 36, textAlign: 'center' }}>#</Box>
                                            <SortHead k="name">First Name</SortHead>
                                            <Box component="th" sx={headSx}>Last Name</Box>
                                            <Box component="th" sx={headSx}>Teacher Email</Box>
                                            <SortHead k="class">Class(es)</SortHead>
                                            <SortHead k="status">Workspace</SortHead>
                                            <Box component="th" sx={headSx} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && (
                                            <tr><Box component="td" colSpan={7} sx={{ ...cellSx, textAlign: 'center' }}><CircularProgress size={22} /></Box></tr>
                                        )}
                                        {!loading && visible.length === 0 && (
                                            <tr>
                                                <Box component="td" colSpan={7} sx={{ ...cellSx, textAlign: 'center', color: MUTED, py: 4 }}>
                                                    {teachers.length ? 'No teachers match your search.' : 'No LearnCreator teachers yet. Use Add Teachers to get started.'}
                                                </Box>
                                            </tr>
                                        )}
                                        {!loading && visible.map((t, i) => {
                                            const n = (t.section_codes || []).length;
                                            return (
                                                <tr key={t.email}>
                                                    <Box component="td" sx={{ ...cellSx, textAlign: 'center', color: MUTED }}>{i + 1}</Box>
                                                    <Box component="td" sx={cellSx}>{t.first_name}</Box>
                                                    <Box component="td" sx={cellSx}>{t.last_name}</Box>
                                                    <Box component="td" sx={{ ...cellSx, wordBreak: 'break-all' }}>{t.email}</Box>
                                                    <Box component="td" sx={cellSx}>
                                                        <Typography sx={{ fontSize: '13px' }}>{n ? plural(n, 'class') : 'No classes'}</Typography>
                                                        <Typography
                                                            component="button"
                                                            onClick={() => setClassesFor(t)}
                                                            sx={{ fontSize: '12.5px', color: ORANGE, background: 'none', border: 0, p: 0, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                                        >
                                                            View/edit class list
                                                        </Typography>
                                                    </Box>
                                                    <Box component="td" sx={cellSx}><TeacherChip published={isPublishedTeacher(t)} /></Box>
                                                    <Box component="td" sx={{ ...cellSx, whiteSpace: 'nowrap', textAlign: 'right' }}>
                                                        <Button onClick={() => setEditing(t)} sx={{ ...smallBtn, border: `1px solid ${ORANGE}`, color: ORANGE, mr: 1 }}>Edit</Button>
                                                        <Button onClick={() => setRemoving(t)} sx={{ ...smallBtn, border: `1px solid ${BORDER}`, color: 'var(--brand-text, #333)' }}>Remove</Button>
                                                    </Box>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: `1px solid ${BORDER}` }}>
                                <Button onClick={onClose} sx={primaryBtn}>Done</Button>
                            </Box>
                        </Box>
                    )}

                    {tab === 'add' && (
                        <Box sx={{ mt: 2.5 }}>
                            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', overflowX: 'auto' }}>
                                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                                    <thead>
                                        <tr>
                                            <Box component="th" sx={{ ...headSx, width: 36, textAlign: 'center' }}>#</Box>
                                            <Box component="th" sx={headSx}>First Name *</Box>
                                            <Box component="th" sx={headSx}>Last Name *</Box>
                                            <Box component="th" sx={headSx}>Teacher Email *</Box>
                                            <Box component="th" sx={{ ...headSx, width: 220 }}>Class(es)</Box>
                                            <Box component="th" sx={{ ...headSx, width: 40 }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r, idx) => {
                                            const err = (r.firstName || r.lastName || r.email) ? rowError(r, idx) : '';
                                            return (
                                                <tr key={idx}>
                                                    <Box component="td" sx={{ ...cellSx, textAlign: 'center', color: MUTED }}>{idx + 1}</Box>
                                                    <Box component="td" sx={cellSx}>
                                                        <TextField size="small" fullWidth placeholder="First Name" value={r.firstName}
                                                            onChange={(e) => updateRow(idx, 'firstName', e.target.value)} sx={inputSx} />
                                                    </Box>
                                                    <Box component="td" sx={cellSx}>
                                                        <TextField size="small" fullWidth placeholder="Last Name" value={r.lastName}
                                                            onChange={(e) => updateRow(idx, 'lastName', e.target.value)} sx={inputSx} />
                                                    </Box>
                                                    <Box component="td" sx={cellSx}>
                                                        <TextField
                                                            size="small" fullWidth placeholder="Teacher Email" value={r.email}
                                                            onChange={(e) => updateRow(idx, 'email', e.target.value)} sx={inputSx}
                                                            error={!!err && err !== 'incomplete'}
                                                            helperText={err && err !== 'incomplete' ? err : ''}
                                                        />
                                                    </Box>
                                                    <Box component="td" sx={cellSx}>
                                                        <GroupedMultiSelect
                                                            options={classOptions(classes, { schoolNameFor })}
                                                            value={r.sectionCodes}
                                                            onChange={(v) => updateRow(idx, 'sectionCodes', v)}
                                                            placeholder="Select"
                                                            renderSelected={(sel) => sel.map((v) => byCode.get(String(v))?.section_name || v).join(', ')}
                                                        />
                                                    </Box>
                                                    <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : [{ ...EMPTY_ROW }]))}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5 }}>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={() => setRows((prev) => [...prev, { ...EMPTY_ROW }])}
                                    sx={{ textTransform: 'none', fontWeight: 700, color: ORANGE }}
                                >
                                    Add Row
                                </Button>
                                <Typography sx={{ fontSize: '13px', color: MUTED }}>
                                    {plural(ready.length, 'new teacher')} ready to add{incomplete > 0 ? ` · ${incomplete} incomplete` : ''}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: '12.5px', color: MUTED, mt: 1.5 }}>
                                Classes are optional — you can add them now or anytime later. New teachers appear under Existing teachers, ready to publish.
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3, pt: 2, borderTop: `1px solid ${BORDER}` }}>
                                <Button onClick={onClose} sx={secondaryBtn}>Cancel</Button>
                                <Button onClick={() => setConfirmAdd(true)} disabled={!ready.length || blocked} sx={primaryBtn}>
                                    {ready.length > 1 ? `Add ${ready.length} teachers` : 'Add teacher'}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={confirmAdd}
                title={ready.length === 1 ? 'Add 1 teacher?' : `Add ${ready.length} teachers?`}
                body="They'll appear under Existing Teachers. No emails are sent until you publish their workspace."
                confirmLabel={ready.length === 1 ? 'Add teacher' : 'Add teachers'}
                onConfirm={addTeachers}
                onCancel={() => setConfirmAdd(false)}
                busy={adding}
            />

            <ConfirmDialog
                open={!!removing}
                icon={(
                    <Box sx={{ width: 44, height: 44, borderRadius: '50%', backgroundColor: 'rgba(192,57,43,0.1)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                        <Typography sx={{ color: DANGER, fontWeight: 800, fontSize: '20px' }}>!</Typography>
                    </Box>
                )}
                title={`Remove ${fullName(removing)}?`}
                body={(
                    <>
                        <div>They'll be removed from {removingClassNames.length ? removingClassNames.join(', ') : 'no classes'}.</div>
                        {isPublishedTeacher(removing) && (
                            <div style={{ marginTop: 6 }}>Their published workspace will be deactivated and they'll lose access.</div>
                        )}
                        <div style={{ marginTop: 6 }}>This can't be undone.</div>
                    </>
                )}
                confirmLabel="Remove teacher"
                danger
                onConfirm={remove}
                onCancel={() => setRemoving(null)}
                busy={removeBusy}
            />

            <EditTeacherDialog
                open={!!editing}
                teacher={editing}
                classes={classes}
                orgCode={orgCode}
                onClose={() => setEditing(null)}
                onSaved={onChanged}
                onManageClasses={() => { const t = editing; setEditing(null); setClassesFor(t); }}
            />

            <TeacherClassesDialog
                open={!!classesFor}
                teacher={classesFor}
                classes={classes}
                schoolNameFor={schoolNameFor}
                orgCode={orgCode}
                onClose={() => setClassesFor(null)}
                onSaved={onChanged}
            />

            <PublishWorkspacesDialog
                open={publishOpen}
                teachers={teachers}
                classes={classes}
                orgCode={orgCode}
                onClose={() => setPublishOpen(false)}
                onPublished={onChanged}
            />
        </>
    );
};

export default ManageLearnCreatorTeachersDialog;
